import { useState, useMemo, useCallback } from 'react'
import {
  Calendar,
  momentLocalizer,
  View,
  SlotInfo,
  Event as CalendarEvent,
} from 'react-big-calendar'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { ACTIVITY_TYPE_COLORS, CATEGORICAL } from '@/lib/theme'
import { Event } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Calendar as CalendarIcon, MapPin, User } from 'lucide-react'

const localizer = momentLocalizer(moment)

interface EventCalendarProps {
  events: Event[]
  onSelectEvent: (event: Event) => void
  onSelectSlot?: (slotInfo: SlotInfo) => void
  onRangeChange?: (range: Date[] | { start: Date; end: Date }) => void
  loading?: boolean
  view?: View
  onViewChange?: (view: View) => void
}

interface CalendarEventData extends CalendarEvent {
  id: string
  title: string
  start: Date
  end: Date
  allDay?: boolean
  resource?: Event
}

export function EventCalendar({
  events,
  onSelectEvent,
  onSelectSlot,
  onRangeChange,
  loading = false,
  view = 'month',
  onViewChange,
}: EventCalendarProps) {
  const [currentView, setCurrentView] = useState<View>(view)
  const [currentDate, setCurrentDate] = useState(new Date())

  // Transform events to calendar format
  const calendarEvents = useMemo<CalendarEventData[]>(() => {
    return events.map((event) => ({
      id: event.id,
      title: event.subject || 'Untitled Event',
      start: new Date(event.start_date_time),
      end: new Date(event.end_date_time),
      allDay: event.is_all_day_event,
      resource: event,
    }))
  }, [events])

  // Custom event style
  const eventStyleGetter = useCallback((event: CalendarEventData) => {
    const resource = event.resource
    if (!resource) return {}

    // GRK-BRAND-6: event-block colours from the palette (all dark enough for the
    // white event text). Type by activity palette; show_as overrides.
    let backgroundColor: string = ACTIVITY_TYPE_COLORS.Task.solid // Navy (default)

    switch (resource.type) {
      case 'Meeting':
        backgroundColor = ACTIVITY_TYPE_COLORS.Event.solid // Evergreen
        break
      case 'Call':
        backgroundColor = ACTIVITY_TYPE_COLORS.Call.solid // Teal
        break
      case 'Email':
        backgroundColor = ACTIVITY_TYPE_COLORS.Email.solid // Plum
        break
      case 'Other':
        backgroundColor = CATEGORICAL.slate.solid
        break
    }

    if (resource.show_as === 'Out of Office') {
      backgroundColor = CATEGORICAL.raspberry.solid
    } else if (resource.show_as === 'Tentative') {
      backgroundColor = CATEGORICAL.steel.solid
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    }
  }, [])

  // Custom toolbar component
  const CustomToolbar = ({ label, onNavigate, onView }: any) => {
    return (
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 text-sm border rounded hover:bg-muted"
            onClick={() => onNavigate('TODAY')}
            aria-label="Go to today"
          >
            Today
          </button>
          <button
            className="px-2 py-1 text-sm border rounded hover:bg-muted"
            onClick={() => onNavigate('PREV')}
            aria-label="Previous period"
          >
            ‹
          </button>
          <button
            className="px-2 py-1 text-sm border rounded hover:bg-muted"
            onClick={() => onNavigate('NEXT')}
            aria-label="Next period"
          >
            ›
          </button>
          <span className="ml-4 text-lg font-semibold">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className={`px-3 py-1 text-sm border rounded hover:bg-muted ${
              currentView === 'month' ? 'bg-muted' : ''
            }`}
            onClick={() => {
              onView('month')
              setCurrentView('month')
            }}
            aria-label="Month view"
          >
            Month
          </button>
          <button
            className={`px-3 py-1 text-sm border rounded hover:bg-muted ${
              currentView === 'week' ? 'bg-muted' : ''
            }`}
            onClick={() => {
              onView('week')
              setCurrentView('week')
            }}
            aria-label="Week view"
          >
            Week
          </button>
          <button
            className={`px-3 py-1 text-sm border rounded hover:bg-muted ${
              currentView === 'day' ? 'bg-muted' : ''
            }`}
            onClick={() => {
              onView('day')
              setCurrentView('day')
            }}
            aria-label="Day view"
          >
            Day
          </button>
          <button
            className={`px-3 py-1 text-sm border rounded hover:bg-muted ${
              currentView === 'agenda' ? 'bg-muted' : ''
            }`}
            onClick={() => {
              onView('agenda')
              setCurrentView('agenda')
            }}
            aria-label="Agenda view"
          >
            Agenda
          </button>
        </div>
      </div>
    )
  }

  // Custom event component
  const CustomEvent = ({ event }: { event: CalendarEventData }) => {
    const resource = event.resource
    if (!resource) return <span>{event.title}</span>

    return (
      <div className="p-1">
        <div className="font-semibold text-xs truncate">{event.title}</div>
        {currentView !== 'month' && (
          <div className="text-xs opacity-90">
            {moment(event.start).format('h:mm A')}
            {resource.location && <span className="ml-1">• {resource.location}</span>}
          </div>
        )}
      </div>
    )
  }

  // Custom agenda event component
  const CustomAgendaEvent = ({ event }: { event: CalendarEventData }) => {
    const resource = event.resource
    if (!resource) return <span>{event.title}</span>

    return (
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="font-semibold">{event.title}</div>
          {resource.description && (
            <div className="text-sm text-muted-foreground mt-1">{resource.description}</div>
          )}
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            {resource.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {resource.location}
              </span>
            )}
            {resource.owner_name && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {resource.owner_name}
              </span>
            )}
            {resource.attendee_count && resource.attendee_count > 0 && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {resource.attendee_count} attendees
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {resource.type && (
            <Badge variant="outline" className="text-xs">
              {resource.type}
            </Badge>
          )}
          {resource.show_as && resource.show_as !== 'Busy' && (
            <Badge variant="secondary" className="text-xs">
              {resource.show_as}
            </Badge>
          )}
        </div>
      </div>
    )
  }

  const handleSelectEvent = useCallback(
    (event: CalendarEventData) => {
      if (event.resource) {
        onSelectEvent(event.resource)
      }
    },
    [onSelectEvent]
  )

  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      if (onRangeChange) {
        onRangeChange(range)
      }
    },
    [onRangeChange]
  )

  const handleViewChange = useCallback(
    (newView: View) => {
      setCurrentView(newView)
      if (onViewChange) {
        onViewChange(newView)
      }
    },
    [onViewChange]
  )

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-[600px]">
          <div className="text-muted-foreground">Loading events...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5" />
          Event Calendar
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ height: 600 }}>
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            view={currentView}
            onView={handleViewChange}
            date={currentDate}
            onNavigate={setCurrentDate}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            onSelectEvent={handleSelectEvent}
            onSelectSlot={onSelectSlot}
            onRangeChange={handleRangeChange}
            selectable={!!onSelectSlot}
            popup
            eventPropGetter={eventStyleGetter}
            components={{
              toolbar: CustomToolbar,
              event: CustomEvent,
              agenda: {
                event: CustomAgendaEvent,
              },
            }}
            messages={{
              today: 'Today',
              previous: 'Back',
              next: 'Next',
              month: 'Month',
              week: 'Week',
              day: 'Day',
              agenda: 'Agenda',
              date: 'Date',
              time: 'Time',
              event: 'Event',
              noEventsInRange: 'There are no events in this range.',
              showMore: (total) => `+${total} more`,
            }}
          />
        </div>
      </CardContent>
    </Card>
  )
}
